import type { Catalogue } from '@daghfal/core';

/**
 * The staff app's messages.
 *
 * Short, imperative, and in the words a teacher already uses — these labels sit on buttons pressed dozens
 * of times a day with one hand while holding a child with the other.
 */
export const strings: Catalogue = {
  ar: {
    'app.name': 'دغفل',
    'app.subtitle': 'تطبيق المعلّمات',

    'nav.class': 'صفّي',
    'nav.attendance': 'الحضور',
    'nav.messages': 'الرسائل',
    'nav.queue': 'قيد الإرسال',
    'nav.medications': 'الأدوية',
    'nav.photos': 'صوري',
    'nav.calendar': 'التقويم',
    'photos.mine': 'الصور التي رفعتُها',
    'photos.mine.lede': 'إن وسمتَ طفلاً خطأً فصحّح الصورة، فتختفي من معرض أسرته وتصل إلى الأسرة الصحيحة. ويمكنك حذف ما رفعتَه.',

    'class.title': 'صفّي',
    'class.switch': 'تبديل الصف',
    'class.none': 'لم يُسنَد إليك أي صف بعد. تواصل مع إدارة الحضانة.',
    'class.empty': 'لا يوجد أطفال في هذا الصف.',
    'class.summary': '{enrolled} من {capacity}',
    'class.missingEntries': 'ينقص: {items}',
    'class.allDone': 'اكتملت تسجيلات اليوم',

    'child.title': 'تسجيل سريع',
    'child.back': 'رجوع إلى الصف',
    'incident.back': 'رجوع إلى {name}',

    // The quick actions the client specified, plus the nappy action (FR-STF-03).
    'action.meal': 'أكل',
    'action.drink': 'شرب',
    'action.sleep': 'نوم',
    'action.activity': 'نشاط',
    'action.mood': 'مزاج',
    'action.diaper': 'حفاض',
    'action.photo': 'صورة',
    'action.note': 'ملاحظة',
    'action.recordedToday': 'سُجِّل اليوم',

    'bulk.title': 'تسجيل جماعي',
    'bulk.hint': 'اختر الأطفال ثم سجّل الإدخال نفسه لهم جميعاً.',
    'bulk.selected': 'المحدَّدون: {count}',
    'bulk.selectAll': 'تحديد الكل',
    'bulk.clear': 'إلغاء التحديد',
    'bulk.start': 'تسجيل لعدة أطفال',
    'bulk.record.one': 'تسجيل لطفل واحد',
    'bulk.record.two': 'تسجيل للطفلين',
    'bulk.record.few': 'تسجيل لـ{count} أطفال',
    'bulk.record.many': 'تسجيل لـ{count} طفلاً',
    'bulk.record.other': 'تسجيل لـ{count} طفل',
    'bulk.choose': 'اختيار {name}',
    'bulk.isChosen': '{name}: مُختار',

    'group.title.one': 'التسجيل لطفل واحد',
    'group.title.two': 'التسجيل لطفلين',
    'group.title.few': 'التسجيل لـ{count} أطفال',
    'group.title.many': 'التسجيل لـ{count} طفلاً',
    'group.title.other': 'التسجيل لـ{count} طفل',
    'group.none': 'لم يُختر أي طفل. ارجع إلى الصف واختر الأطفال أولاً.',
    'group.recordedFor': 'سُجّل لكل من: {names}.',

    'entry.timeNow': 'الوقت: الآن',
    'entry.changeTime': 'تغيير الوقت',
    'entry.otherFood': 'أصناف أخرى (افصل بينها بفاصلة)',

    'class.asleep': '😴 نائم/ة منذ {time}',
    'class.draft': 'مسودّة',
    'class.publish': 'نشر اليوم',

    'queue.title': 'قيد الإرسال',
    'queue.empty': 'لا توجد إدخالات بانتظار الإرسال.',
    'queue.pending': '{count} إدخال بانتظار الاتصال',
    'queue.hint': 'تُحفظ إدخالاتك على الجهاز وتُرسَل تلقائياً عند عودة الاتصال. لن يضيع أي إدخال.',
    'queue.retry': 'محاولة الإرسال الآن',
    'queue.notPresent': 'لم يُسجَّل وصول هؤلاء الأطفال في وقت هذا الإدخال. سجّل وصولهم ثم أعد الإرسال.',
    'queue.heldFor': 'بانتظار: {names}',

    'attendance.title': 'الحضور',
    'messages.title': 'الرسائل',

    // The photo editor (Q1). «تعديل الصورة» is also the name the API's consent refusal sends teachers to.
    'editor.open': 'تعديل الصورة',
    'editor.title': 'قصّ الصورة وتعتيم الوجوه',
    'editor.tools': 'الأداة',
    'editor.tool.blurArea': 'تعتيم مساحة',
    'editor.tool.blurStroke': 'فرشاة التعتيم',
    'editor.tool.crop': 'قصّ',
    'editor.hint.blurArea': 'اسحب فوق كل وجه لا يجوز أن يظهر.',
    'editor.hint.blurStroke': 'امسح بإصبعك فوق ما تريد إخفاءه.',
    'editor.hint.crop': 'اسحب حول الجزء الذي تريد إبقاءه. يُحذف ما حوله.',
    'editor.canvas': 'الصورة. اسحب عليها لتطبيق الأداة المختارة.',
    'editor.undo': 'تراجع',
    'editor.reset': 'البدء من جديد',
    'editor.apply': 'اعتماد التعديل',
    'editor.preparing': 'جارٍ تجهيز الصورة…',
    'editor.cancel': 'إلغاء',
    'editor.loading': 'جارٍ فتح الصورة…',
    'editor.unreadable': 'تعذّر فتح هذه الصورة للتعديل في هذا المتصفح. إن ظهر فيها أطفال آخرون فلا ترفعها: التقطها من جديد بحيث لا يظهر فيها غير هذا الطفل.',
    'editor.failed': 'تعذّر تجهيز الصورة المعدّلة. حاول مرة أخرى، أو التقط صورة أصغر.',
    'editor.applied': 'عُدّلت الصورة. يُرفع ما تراه هنا فقط، ولا تغادر الصورة الأصلية هاتفك.',
    'editor.hint.single': 'إن ظهر في الصورة أطفال آخرون فاقتصّها أو عتّم وجوههم بزر «تعديل الصورة» قبل الرفع.',
    'editor.hint.shared': 'الطفل الرمادي في القائمة لا يُشار إليه. إن ظهر في الصورة فعتّم وجهه بزر «تعديل الصورة».',
    'editor.hint.video': 'الفيديو لا يُقصّ ولا يُعتَّم هنا. إن ظهر فيه أطفال آخرون فصوّر مقطعاً آخر.',
  },

  en: {
    'app.name': 'Daghfal',
    'app.subtitle': 'Staff app',

    'nav.class': 'My class',
    'nav.attendance': 'Attendance',
    'nav.messages': 'Messages',
    'nav.queue': 'Pending',
    'nav.medications': 'Medication',
    'nav.photos': 'My photos',
    'nav.calendar': 'Calendar',
    'photos.mine': 'Photos I uploaded',
    'photos.mine.lede': 'Tagged the wrong child? Correct the photo: it leaves that family’s gallery and reaches the right one. You can also delete what you uploaded.',

    'class.title': 'My class',
    'class.switch': 'Switch class',
    'class.none': 'No class has been assigned to you yet. Please contact the nursery.',
    'class.empty': 'No children in this class.',
    'class.summary': '{enrolled} of {capacity}',
    'class.missingEntries': 'Missing: {items}',
    'class.allDone': 'Today’s entries are complete',

    'child.title': 'Quick entry',
    'child.back': 'Back to class',
    'incident.back': 'Back to {name}',

    'action.meal': 'Meal',
    'action.drink': 'Drink',
    'action.sleep': 'Sleep',
    'action.activity': 'Activity',
    'action.mood': 'Mood',
    'action.diaper': 'Nappy',
    'action.photo': 'Photo',
    'action.note': 'Note',
    'action.recordedToday': 'Recorded today',

    'bulk.title': 'Record for several children',
    'bulk.hint': 'Choose the children, then record the same entry for all of them.',
    'bulk.selected': 'Selected: {count}',
    'bulk.selectAll': 'Select all',
    'bulk.clear': 'Clear',
    'bulk.start': 'Record for several children',
    'bulk.record.one': 'Record for one child',
    'bulk.record.other': 'Record for {count} children',
    'bulk.choose': 'Choose {name}',
    'bulk.isChosen': '{name}: chosen',

    'group.title.one': 'Recording for one child',
    'group.title.other': 'Recording for {count} children',
    'group.none': 'No child is chosen. Go back to the class and choose the children first.',
    'group.recordedFor': 'Recorded for: {names}.',

    'entry.timeNow': 'Time: now',
    'entry.changeTime': 'Change the time',
    'entry.otherFood': 'Other food (separate with commas)',

    'class.asleep': '😴 Asleep since {time}',
    'class.draft': 'Draft',
    'class.publish': 'Publish the day',

    'queue.title': 'Pending',
    'queue.notPresent': 'These children were not checked in at the time of this entry. Check them in, then send it again.',
    'queue.heldFor': 'Waiting for: {names}',
    'queue.empty': 'Nothing is waiting to be sent.',
    'queue.pending': '{count} entries waiting for a connection',
    'queue.hint': 'Your entries are saved on this device and sent automatically when you are back online. Nothing is lost.',
    'queue.retry': 'Try sending now',

    'attendance.title': 'Attendance',
    'messages.title': 'Messages',

    'editor.open': 'Edit photo',
    'editor.title': 'Crop the photo and blur faces',
    'editor.tools': 'Tool',
    'editor.tool.blurArea': 'Blur an area',
    'editor.tool.blurStroke': 'Blur brush',
    'editor.tool.crop': 'Crop',
    'editor.hint.blurArea': 'Drag over every face that must not show.',
    'editor.hint.blurStroke': 'Rub your finger over what you want to hide.',
    'editor.hint.crop': 'Drag around the part you want to keep. Everything around it goes.',
    'editor.canvas': 'The photo. Drag on it to use the chosen tool.',
    'editor.undo': 'Undo',
    'editor.reset': 'Start again',
    'editor.apply': 'Use this edit',
    'editor.preparing': 'Preparing the photo…',
    'editor.cancel': 'Cancel',
    'editor.loading': 'Opening the photo…',
    'editor.unreadable': 'This browser cannot open this photo for editing. If other children are in it, do not upload it: take it again so that only this child shows.',
    'editor.failed': 'The edited photo could not be prepared. Try again, or take a smaller photo.',
    'editor.applied': 'The photo is edited. Only what you see here is uploaded; the original never leaves your phone.',
    'editor.hint.single': 'If other children are in the photo, crop it or blur their faces with Edit photo before you upload it.',
    'editor.hint.shared': 'A greyed-out child cannot be tagged. If they are in the photo, blur their face with Edit photo.',
    'editor.hint.video': 'A video cannot be cropped or blurred here. If other children are in it, record another clip.',
  },
};
